/**
 * Device Fingerprinting Utility
 * Generates a unique fingerprint for the device/browser for rate limiting purposes
 */

// Generate a simple device fingerprint based on browser characteristics
export const generateDeviceFingerprint = () => {
  const canvas = document.createElement('canvas');
  const ctx = canvas.getContext('2d');
  ctx.textBaseline = 'top';
  ctx.font = '14px Arial';
  ctx.fillText('Device fingerprint', 2, 2);
  
  const fingerprint = {
    userAgent: navigator.userAgent,
    language: navigator.language,
    platform: navigator.platform,
    cookieEnabled: navigator.cookieEnabled,
    doNotTrack: navigator.doNotTrack,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screen: {
      width: window.screen.width,
      height: window.screen.height,
      colorDepth: window.screen.colorDepth,
      pixelDepth: window.screen.pixelDepth
    },
    canvas: canvas.toDataURL(),
    webgl: getWebGLFingerprint(),
    plugins: getPluginsFingerprint(),
    localStorage: isLocalStorageAvailable(),
    sessionStorage: isSessionStorageAvailable(),
    indexedDB: isIndexedDBAvailable(),
    hardwareConcurrency: navigator.hardwareConcurrency || 0,
    deviceMemory: navigator.deviceMemory || 0,
    connection: getConnectionInfo()
  };

  // Create a hash of the fingerprint
  return hashFingerprint(JSON.stringify(fingerprint));
};

// Get WebGL fingerprint
const getWebGLFingerprint = () => {
  try {
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
    
    if (!gl) return null;
    
    const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
    return {
      vendor: gl.getParameter(debugInfo?.UNMASKED_VENDOR_WEBGL || gl.VENDOR),
      renderer: gl.getParameter(debugInfo?.UNMASKED_RENDERER_WEBGL || gl.RENDERER),
      version: gl.getParameter(gl.VERSION),
      shadingLanguageVersion: gl.getParameter(gl.SHADING_LANGUAGE_VERSION)
    };
  } catch (e) {
    return null;
  }
};

// Get plugins fingerprint
const getPluginsFingerprint = () => {
  try {
    return Array.from(navigator.plugins).map(plugin => ({
      name: plugin.name,
      filename: plugin.filename,
      description: plugin.description
    }));
  } catch (e) {
    return [];
  }
};

// Check storage availability
const isLocalStorageAvailable = () => {
  try {
    localStorage.setItem('test', 'test');
    localStorage.removeItem('test');
    return true;
  } catch (e) {
    return false;
  }
};

const isSessionStorageAvailable = () => {
  try {
    sessionStorage.setItem('test', 'test');
    sessionStorage.removeItem('test');
    return true;
  } catch (e) {
    return false;
  }
};

const isIndexedDBAvailable = () => {
  try {
    return !!window.indexedDB;
  } catch (e) {
    return false;
  }
};

// Get connection information
const getConnectionInfo = () => {
  try {
    const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!connection) return null;
    
    return {
      effectiveType: connection.effectiveType,
      downlink: connection.downlink,
      rtt: connection.rtt,
      saveData: connection.saveData
    };
  } catch (e) {
    return null;
  }
};

// Simple hash function for fingerprint
const hashFingerprint = (str) => {
  let hash = 0;
  if (str.length === 0) return hash.toString();
  
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return Math.abs(hash).toString(36);
};

// Store fingerprint in localStorage for persistence
export const getStoredFingerprint = () => {
  try {
    return localStorage.getItem('deviceFingerprint');
  } catch (e) {
    return null;
  }
};

export const storeFingerprint = (fingerprint) => {
  try {
    localStorage.setItem('deviceFingerprint', fingerprint);
  } catch (e) {
    // Silently fail if localStorage is not available
  }
};

// Get or generate device fingerprint
export const getDeviceFingerprint = () => {
  let fingerprint = getStoredFingerprint();
  
  if (!fingerprint) {
    fingerprint = generateDeviceFingerprint();
    storeFingerprint(fingerprint);
  }
  
  return fingerprint;
};